import React, { useEffect, useState } from "react";
import {
  Col,
  Row,
  Form,
  Input,
  Button,
  Layout,
  Select,
  Upload,
  Typography,
  message,
} from "antd";
import { FaArrowLeft } from "react-icons/fa";
import { InboxOutlined } from "@ant-design/icons";
import { Get } from "../../config/api/get";
import { Put } from "../../config/api/put";
import { ASSETS, COURSE, CONTENT_TYPE } from "../../config/constants/api";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import swal from "sweetalert";

const { TextArea } = Input;

const ASSET_TYPES = [
  { value: "IMAGE", label: "IMAGE" },
  { value: "VIDEO", label: "VIDEO" },
  { value: "PDF", label: "PDF" },
];

const STATUS_OPTIONS = [
  { value: "ACTIVE", label: "ACTIVE" },
  { value: "INACTIVE", label: "INACTIVE" },
];

const ACCEPT_BY_TYPE = {
  IMAGE: "image/*",
  VIDEO: "video/*",
  PDF: ".pdf,application/pdf",
};

function AssetEdit() {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const token = useSelector((state) => state.user.userToken);
  const userData = useSelector((state) => state.user.userData);
  const asset = location.state?.asset ?? null;
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [courses, setCourses] = useState([]);
  const [fileList, setFileList] = useState([]);
  const [selectedType, setSelectedType] = useState("IMAGE");

  useEffect(() => {
    loadCourses();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (asset) {
      form.setFieldsValue({
        title: asset.title,
        description: asset.description || "",
        type: asset.type || "IMAGE",
        status: asset.status || "ACTIVE",
        courses: (asset.courses || []).map((c) => (typeof c === "object" ? c._id : c)).filter(Boolean),
      });
      setSelectedType(asset.type || "IMAGE");
    }
  }, [asset, form]);

  const loadCourses = async () => {
    try {
      const res = await Get(COURSE.getAllCourses, token, { limit: "500", createdBy: userData?._id });
      const data = res?.data ?? res;
      const list = data?.docs ?? data ?? [];
      setCourses(Array.isArray(list) ? list : []);
    } catch (e) {
      message.error("Failed to load courses");
    }
  };

  const beforeUpload = (file) => {
    const type = form.getFieldValue("type") || selectedType;
    const isImage = type === "IMAGE" && file.type.startsWith("image/");
    const isVideo = type === "VIDEO" && file.type.startsWith("video/");
    const isPdf = type === "PDF" && (file.type === "application/pdf" || file.name?.toLowerCase().endsWith(".pdf"));
    if (!isImage && !isVideo && !isPdf) {
      message.error(`File type must match asset type "${type}"`);
      return Upload.LIST_IGNORE;
    }
    setFileList([file]);
    return false;
  };

  const onFinish = (values) => {
    const type = values.type || "IMAGE";
    const file = fileList[0];

    if (file) {
      const isImage = type === "IMAGE" && file.type?.startsWith("image/");
      const isVideo = type === "VIDEO" && file.type?.startsWith("video/");
      const isPdf = type === "PDF" && (file.type === "application/pdf" || file.name?.toLowerCase().endsWith(".pdf"));
      if (!isImage && !isVideo && !isPdf) {
        message.error(`File type must match asset type "${type}"`);
        return;
      }
    }

    const formData = new FormData();
    if (type === "PDF") {
      formData.append("files", file);
    } else if (type === "IMAGE") {
      formData.append("image", file);
    } else if (type === "VIDEO") {
      formData.append("video", file);
    }
    formData.append("title", values.title);
    formData.append("description", values.description || "");
    formData.append("type", type);
    formData.append("status", values.status || "ACTIVE");
    if (values.courses && values.courses.length) {
      values.courses.forEach((courseId) => formData.append("courses", courseId));
    }

    setLoading(true);
    Put(ASSETS.updateAsset + id, formData, token, null, CONTENT_TYPE.FORM_DATA)
      .then((res) => {
        setLoading(false);
        const data = res?.data ?? res;
        if (data?.status) {
          swal("Success!", "Asset updated successfully", "success");
          navigate("/asset-management");
        } else {
          swal("Error", data?.message || "Failed to update asset", "error");
        }
      })
      .catch(() => setLoading(false));
  };

  if (!asset) {
    return (
      <Layout className="configuration">
        <div className="boxDetails" style={{ padding: 40 }}>
          <p>No asset data. Please open this page from the Asset list or Details (Edit).</p>
          <Button type="primary" className="loginButton" onClick={() => navigate("/asset-management")}>
            Back to Assets
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout className="configuration">
      <div className="boxDetails">
        <Row
          className="whiteBackground"
          style={{ backgroundColor: "white", justifyContent: "center" }}
        >
          <Col xs={24} md={22}>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                flexDirection: "column",
                padding: "100px 20px",
              }}
            >
              <Row style={{ padding: "10px 20px" }}>
                <Col xs={24} md={12} style={{ display: "flex", alignItems: "center" }}>
                  <FaArrowLeft
                    style={{ fontWeight: "bold", fontSize: "20px", cursor: "pointer" }}
                    onClick={() => navigate(-1)}
                  />
                  &emsp;
                  <h1 className="pageTitle" style={{ margin: 0 }}>Edit Asset</h1>
                </Col>
              </Row>
              <br />

              <Form
                form={form}
                onFinish={onFinish}
                autoComplete="off"
              >
                <Row style={{ padding: "20px" }}>
                  <Col xs={24} md={18}>

                    {/* Row 1: Title + Type */}
                    <Row gutter={20}>
                      <Col xs={24} sm={12} style={{ display: "flex", alignItems: "flex-start", flexDirection: "column" }}>
                        <Typography.Title
                          className="fontFamily1"
                          style={{ fontSize: "16px", fontWeight: 600, color: "black", textAlign: "left", marginBottom: 20 }}
                        >
                          Title :
                        </Typography.Title>
                        <Form.Item
                          name="title"
                          style={{ width: "100%" }}
                          rules={[{ required: true, message: "Please enter title" }]}
                        >
                          <Input
                            size="large"
                            placeholder="Enter asset title"
                            className="signupFormInput"
                          />
                        </Form.Item>
                      </Col>

                      <Col xs={24} sm={12} style={{ display: "flex", alignItems: "flex-start", flexDirection: "column" }}>
                        <Typography.Title
                          className="fontFamily1"
                          style={{ fontSize: "16px", fontWeight: 600, color: "black", textAlign: "left", marginBottom: 20 }}
                        >
                          Type :
                        </Typography.Title>
                        <Form.Item
                          name="type"
                          style={{ width: "100%" }}
                          rules={[{ required: true, message: "Please select a type" }]}
                        >
                          <Select
                            size="large"
                            placeholder="Select type"
                            className="signupSelectBox"
                            options={ASSET_TYPES}
                            onChange={(v) => {
                              setSelectedType(v);
                              setFileList([]);
                            }}
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    {/* Row 2: Status + Courses */}
                    <Row gutter={20}>
                      <Col xs={24} sm={12} style={{ display: "flex", alignItems: "flex-start", flexDirection: "column" }}>
                        <Typography.Title
                          className="fontFamily1"
                          style={{ fontSize: "16px", fontWeight: 600, color: "black", textAlign: "left", marginBottom: 20 }}
                        >
                          Status :
                        </Typography.Title>
                        <Form.Item name="status" style={{ width: "100%" }}>
                          <Select
                            size="large"
                            className="signupSelectBox"
                            options={STATUS_OPTIONS}
                          />
                        </Form.Item>
                      </Col>

                      <Col xs={24} sm={12} style={{ display: "flex", alignItems: "flex-start", flexDirection: "column" }}>
                        <Typography.Title
                          className="fontFamily1"
                          style={{ fontSize: "16px", fontWeight: 600, color: "black", textAlign: "left", marginBottom: 20 }}
                        >
                          Courses :
                        </Typography.Title>
                        <Form.Item name="courses" style={{ width: "100%" }}>
                          <Select
                            mode="multiple"
                            size="large"
                            placeholder="Select courses"
                            className="custum-multiselect courseSelectBox2"
                            optionFilterProp="label"
                            options={courses.map((c) => ({ value: c._id, label: c.title || c.name }))}
                            filterOption={(input, option) =>
                              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                            }
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    {/* Row 3: Description (full width) */}
                    <Row gutter={20}>
                      <Col xs={24} style={{ display: "flex", alignItems: "flex-start", flexDirection: "column" }}>
                        <Typography.Title
                          className="fontFamily1"
                          style={{ fontSize: "16px", fontWeight: 600, color: "black", textAlign: "left", marginBottom: 20 }}
                        >
                          Description :
                        </Typography.Title>
                        <Form.Item name="description" style={{ width: "100%" }}>
                          <TextArea
                            rows={4}
                            placeholder="Enter description"
                            className="signupFormInput"
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    {/* Row 4: File Upload (full width) */}
                    <Row gutter={20}>
                      <Col xs={24} style={{ display: "flex", alignItems: "flex-start", flexDirection: "column" }}>
                        <Typography.Title
                          className="fontFamily1"
                          style={{ fontSize: "16px", fontWeight: 600, color: "black", textAlign: "left", marginBottom: 20 }}
                        >
                          File Upload :
                        </Typography.Title>
                        <Form.Item
                          style={{ width: "100%" }}
                          extra={`Optional — leave empty to keep the current file. Allowed: ${ACCEPT_BY_TYPE[selectedType] || "—"}`}
                        >
                          <Upload.Dragger
                            maxCount={1}
                            fileList={fileList.map((f) => ({ uid: f.uid || "-", name: f.name, status: "done" }))}
                            beforeUpload={beforeUpload}
                            onRemove={() => setFileList([])}
                            accept={ACCEPT_BY_TYPE[selectedType]}
                          >
                            <p className="ant-upload-drag-icon">
                              <InboxOutlined style={{ fontSize: 48, color: "#2484c6" }} />
                            </p>
                            <p className="ant-upload-text">Click or drag file to replace</p>
                            <p className="ant-upload-hint">Leave empty to keep the existing file</p>
                          </Upload.Dragger>
                        </Form.Item>
                      </Col>
                    </Row>

                  </Col>
                </Row>

                {/* Action buttons */}
                <Row style={{ padding: "0 20px 20px" }}>
                  <Col xs={24} md={18}>
                    <Row gutter={20}>
                      <Col xs={24} style={{ display: "flex", alignItems: "flex-start", flexDirection: "column", paddingTop: 10 }}>
                        <div style={{ display: "flex", gap: 12 }}>
                          <Button
                            type="primary"
                            htmlType="submit"
                            className="loginButton"
                            loading={loading}
                          >
                            {loading ? "Updating..." : "Update Asset"}
                          </Button>
                          <Button
                            size="large"
                            shape="round"
                            onClick={() => navigate(-1)}
                          >
                            Cancel
                          </Button>
                        </div>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </Form>

              <br />
              <br />
            </div>
          </Col>
        </Row>
      </div>
    </Layout>
  );
}

export default AssetEdit;
